import { msg } from "../../locales/translate";
export type MembershipStatus = "active" | "deleted" | "blocked" | "unknown";
export const membershipStatus = (value: unknown): MembershipStatus => {
	const status = typeof value === "string" ? value.trim().toLowerCase() : "";
	return status === "active" || status === "deleted" || status === "blocked" ? status : "unknown";
};
export const membershipLabel = (value: unknown) => ({ active: msg("activeInProject"), deleted: msg("removedFromProject"), blocked: msg("blockedInProject"), unknown: msg("projectAccessUnknown") })[membershipStatus(value)];

export interface MembershipContext { userId: number; projectId: number; projectName: string; canManage: boolean }
interface Response { data?: unknown; error?: { message?: string; details?: string } | null }
interface Request { method: string; data: { id: number }; apiVersion: 2; expectedProjectId: number }

// Only uses the existing remove/restore endpoints. Roles, clients and account
// status are never rewritten as a side effect of this frontend action.
export function createMembershipUpdater(deps: { context: () => Promise<MembershipContext>; request: (request: Request) => Promise<Response> }) {
	const pending = new Set<string>();
	return async (id: number, action: "restore" | "remove", expected: MembershipContext): Promise<"changed" | "unchanged" | "pending"> => {
		const key = `${expected.userId}:${expected.projectId}:${id}`;
		if (pending.has(key)) return "pending";
		pending.add(key);
		try {
			const check = async () => {
				const now = await deps.context();
				if (now.userId !== expected.userId || now.projectId !== expected.projectId || !now.canManage || !expected.canManage ||
					!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(expected.projectId) || expected.projectId <= 0 || expected.userId <= 0) throw new Error(msg("projectOrSessionChangedReopenTheUserInTheSelectedProject"));
			};
			const call = async (method: string) => {
				await check();
				const response = await deps.request({ method, data: { id }, apiVersion: 2, expectedProjectId: expected.projectId });
				if (response.error) throw new Error(response.error.details || response.error.message || msg("couldNotUpdateProjectAccess"));
				await check();
				return response.data;
			};
			const desired = action === "restore" ? "active" : "deleted";
			const readStatus = async () => membershipStatus((await call("users/get") as { project_status?: unknown } | null)?.project_status);
			const before = await readStatus();
			if (before === desired) return "unchanged";
			if (before === "unknown" || action === "restore" && before !== "deleted") throw new Error(msg("projectAccessHasChangedRefreshTheUserBeforeContinuing"));
			const changed = await call(`users/${action}`);
			if (changed !== true && changed !== false) throw new Error(msg("unexpectedResponseRefreshTheUserToCheckProjectAccess"));
			if (await readStatus() !== desired) throw new Error(msg("projectAccessWasNotConfirmedRefreshTheUserAndTryAgain"));
			return changed ? "changed" : "unchanged";
		} finally { pending.delete(key); }
	};
}
